/**
 * End-of-content anchor that pages a list as the user reaches the end of the loaded rows, replacing
 * a "Load more" button. Render it as the LAST child of the element that actually scrolls.
 *
 * ANCHORING. The sentinel must sit inside the scroll container and must NOT be a row. A windowed
 * table only mounts the rows in the current window, so a row-anchored sentinel is unmounted the
 * moment it scrolls out of that window and the observer goes blind; and a sentinel placed after the
 * container (where a Load-more button used to sit) is never clipped, so it is visible from the first
 * frame and fires immediately. Anchored to the container it works on every render path.
 *
 * ROOT. The scrolling container, not the viewport — it clips the sentinel, and clipping is not
 * something `rootMargin` can see past, so a viewport root could never start the fetch before the
 * user hits the bottom. With the container as root, `rootMargin`'s bottom inset buys the prefetch.
 * WHICH element scrolls is read off the live DOM rather than taken from the caller: the same list
 * renders both as a page that bounds its own height and as a tab in a layout that leaves it to grow,
 * and in the second case the container the caller owns scrolls nothing at all.
 *
 * The observer comes from the shared {@link useInfiniteScroll} (the same one the dynamic pickers
 * use). Its options are memoized here so a windowed scroll — which can re-render the host on every
 * frame — does NOT tear down and rebuild the observer per frame.
 */
import { useInfiniteScroll } from 'hooks/useInfiniteScroll';
import { t } from 'utils/getTranslations';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './endOfListSentinel.module.css';

/**
 * Slack allowed before a container counts as scrollable at all.
 *
 * The sentinel's own height is cancelled in CSS, but that only cancels its BOX — a flex or grid
 * parent's `gap` still inserts space before it, which the negative margin cannot see (measured: 7px
 * in the classifiers list, whose Stack has `row-gap: 8px`). Without slack, that phantom space alone
 * makes an exactly-fitting list report itself scrollable and pulls a page nobody asked for. A handful
 * of pixels is also not something a user can scroll to an "end" of, so requiring more than this is
 * the same rule stated honestly rather than an allowance for the sentinel specifically.
 */
export const MIN_SCROLLABLE_SLACK_PX = 16;

/**
 * Whether an element is scrolling its own content, i.e. whether it has an "end" the user can reach.
 * The same predicate the fetch gate applies, so picking a scroller and deciding it can page cannot
 * disagree: a container that only overflows by a few pixels of phantom `gap` is neither.
 */
export const isScrollingContainer = (node: HTMLElement | null | undefined): boolean =>
    !!node && node.scrollHeight - node.clientHeight > MIN_SCROLLABLE_SLACK_PX;

/**
 * The ancestor that actually scrolls vertically, for callers whose scroller is not theirs to hand
 * over — a list rendered into a layout that owns the scrolling (e.g. a settings page's content box)
 * has no ref to pass, because the element is not in its own JSX at all.
 *
 * `overflow-y: auto` alone is NOT enough to identify it. A page can nest several such elements where
 * only the outermost is height-bounded: the pricelists list sits in a `TableContainer` inside a
 * `ScrollX`, both `overflow: auto`, both of which simply GROW with their content (`scrollHeight ===
 * clientHeight`) while the page-content box above them is the one that scrolls. Taking the first
 * overflow ancestor there would pick a container that can never reach an end, and the list would
 * never page. So prefer the nearest ancestor that is genuinely scrollable, and keep the nearest
 * overflow container only as a fallback for when nothing overflows yet.
 *
 * That makes the result depend on how much content is loaded, so the caller re-resolves as rows
 * arrive rather than trusting a single reading taken at mount.
 */
export const findScrollParent = (from: HTMLElement): HTMLElement | null => {
    let firstOverflowContainer: HTMLElement | null = null;
    for (let node = from.parentElement; node; node = node.parentElement) {
        if (!/(auto|scroll)/.test(getComputedStyle(node).overflowY)) continue;
        if (isScrollingContainer(node)) return node;
        if (!firstOverflowContainer) firstOverflowContainer = node;
    }
    return firstOverflowContainer;
};

/**
 * How far before the end of the rows the next page starts loading, as a fraction of the container's
 * own height. A FIXED pixel inset does not survive contact with real lists: on a tall container with
 * tall rows, 200px is under two rows, so the fetch only starts once the user has scrolled to within
 * a sliver of the very last row and the wait is fully visible. Scaling with the viewport keeps the
 * lead time roughly "one screen of rows" whatever the row height and window size are.
 */
const PREFETCH_VIEWPORT_RATIO = 1;
/** Floor for the above, so a short container (a picker, an accordion panel) still prefetches. */
const MIN_PREFETCH_PX = 200;

/** Bottom-only root inset; the other edges stay 0 so only approaching the END counts. */
export const prefetchRootMargin = (clientHeight: number): string =>
    `0px 0px ${Math.max(MIN_PREFETCH_PX, Math.round(clientHeight * PREFETCH_VIEWPORT_RATIO))}px 0px`;

/**
 * Whether an intersection of the sentinel should turn into a request for the next page. Pure, so the
 * gating is testable without a layout engine.
 *
 * The scrollability check is the "no fetch on first paint" rule: the sentinel is visible from the
 * first frame whenever the loaded rows do not fill the container, and a list the user cannot scroll
 * has no "end" to reach — so visibility alone must not pull another page. This is why the root has to
 * be the element that genuinely scrolls rather than whichever container the caller owns: gated on one
 * that merely grows with its content, a list could never page at all. The comparison carries
 * {@link MIN_SCROLLABLE_SLACK_PX} of slack so a parent's flex/grid `gap` before the sentinel cannot
 * masquerade as scrollable content.
 *
 * `requestedAtRowCount` is the repeat-fire latch, and it is keyed on the ROW COUNT rather than on a
 * loading flag because a loading flag only proves a request happened, not that it achieved anything.
 * A page that errors leaves the caller's page index unadvanced and `hasNextPage` true forever — the
 * list hooks bump their index inside the refetch's `.then`, so a rejected refetch never advances it.
 * Latching on the row count means one request per distinct set of loaded rows: a failed page costs a
 * single wasted call instead of one on every scroll to the bottom for the rest of the session. The
 * latch re-arms when the sentinel leaves the root box, so scrolling away and back retries, exactly
 * like clicking the old button again.
 */
export const shouldRequestNextPage = ({
    hasNextPage,
    isLoadingMore,
    requestedAtRowCount,
    rowCount,
    scrollHeight,
    clientHeight,
}: {
    hasNextPage: boolean;
    isLoadingMore: boolean;
    /** Row count the in-flight / last request was made at, or null when the latch is open. */
    requestedAtRowCount: number | null;
    rowCount: number;
    scrollHeight: number;
    clientHeight: number;
}): boolean =>
    hasNextPage &&
    !isLoadingMore &&
    requestedAtRowCount !== rowCount &&
    rowCount > 0 &&
    scrollHeight - clientHeight > MIN_SCROLLABLE_SLACK_PX;

export type EndOfListSentinelProps = {
    /** The container the caller believes scrolls, used until the DOM says otherwise — see
     *  {@link findScrollParent}, which returns this same node whenever it is the one scrolling and
     *  something further up when it is not. Optional: a list rendered into a layout that owns the
     *  scrolling has no ref to pass at all. Pass the NODE (not a ref) — an IntersectionObserver root
     *  is read when the observer is built, and a ref still holds `null` on the first render. */
    scrollElement?: HTMLElement | null;
    /** Number of rows currently loaded. Drives the repeat-fire latch. */
    rowCount: number;
    /** Whether a further page exists (the list response's `last`, inverted). */
    hasNextPage: boolean;
    /** True while a page request is in flight. */
    isLoadingMore: boolean;
    /** Requests the next page. Omit to leave the sentinel inert. */
    onLoadMore?: () => void;
};

export const EndOfListSentinel = ({
    scrollElement,
    rowCount,
    hasNextPage,
    isLoadingMore,
    onLoadMore,
}: EndOfListSentinelProps): JSX.Element | null => {
    const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);
    // Row count the last request was fired at; null means armed. Deliberately NOT released by the
    // loading flag settling — see `shouldRequestNextPage`.
    const requestedAtRowCountRef = useRef<number | null>(null);
    // The scroller read off the live DOM. State, not a ref, because it is the observer root: the
    // observer must be rebuilt once the node is known.
    // oxlint-disable-next-line react-doctor/rerender-state-only-in-handlers -- must be state, not a ref: it is the observer root, and the observer is built in an effect that a ref mutation would not re-run
    const [resolvedScroller, setResolvedScroller] = useState<HTMLElement | null>(null);
    // The caller's node only stands in until the DOM has been read: a container handed over by a
    // caller is the scroller only while it is genuinely scrolling, and whether it is depends on the
    // layout it was dropped into, not on who owns the ref. The same list renders both as a page that
    // bounds its own height and as a tab in a layout that leaves it to grow (a case's timecards tab:
    // nothing above the TableContainer constrains it, so it grows with its rows and the page-content
    // box is what scrolls). Pinned to a grown container, `scrollHeight === clientHeight` forever, the
    // gate below never opens, and the list stops dead at its first page.
    const scroller = resolvedScroller ?? scrollElement ?? null;

    // Re-resolved as rows arrive, not just at mount: which ancestor is genuinely scrollable depends
    // on how much content is loaded (see findScrollParent) — and while the caller's own container is
    // the one scrolling, it is also the nearest scrollable ancestor, so this keeps choosing it.
    useEffect(() => {
        if (!sentinel) return;
        const found = findScrollParent(sentinel);
        // oxlint-disable-next-line react-hooks-js/set-state-in-effect -- layout measurement: which ancestor scrolls is read off the committed DOM
        setResolvedScroller((previous) => (previous === found ? previous : found));
    }, [sentinel, scrollElement, rowCount, hasNextPage]);

    const requestNextPage = useCallback(() => {
        if (!onLoadMore || !scroller) return;
        if (
            !shouldRequestNextPage({
                hasNextPage,
                isLoadingMore,
                requestedAtRowCount: requestedAtRowCountRef.current,
                rowCount,
                scrollHeight: scroller.scrollHeight,
                clientHeight: scroller.clientHeight,
            })
        ) {
            return;
        }
        requestedAtRowCountRef.current = rowCount;
        onLoadMore();
    }, [onLoadMore, scroller, hasNextPage, isLoadingMore, rowCount]);

    // Scrolling away from the end re-arms the latch, so returning to the end retries a page that
    // failed — the infinite-scroll equivalent of clicking the old button a second time.
    const handleLeave = useCallback(() => {
        requestedAtRowCountRef.current = null;
    }, []);

    // Memoized so the observer is rebuilt only on a real change (container, row count, page/loading
    // state) — never on a scroll frame. A rebuild re-observes the sentinel, which emits a fresh
    // callback with its CURRENT intersection: that is how the observer re-arms after a page lands (or
    // after a filter change resets the list) while still sitting at the end of the rows.
    const observerOptions = useMemo(
        () => ({
            observer: {
                root: scroller,
                // Read off the live container rather than a constant, so the lead time tracks the
                // rendered viewport. Recomputed whenever the container or the row count changes,
                // which is every load-more — close enough to follow a window resize in practice.
                rootMargin: prefetchRootMargin(scroller?.clientHeight ?? 0),
                threshold: 0,
            },
            disabled: !onLoadMore || !hasNextPage || rowCount === 0,
            isLoading: isLoadingMore,
            onLeave: handleLeave,
        }),
        [scroller, onLoadMore, hasNextPage, rowCount, isLoadingMore, handleLeave]
    );

    useInfiniteScroll(sentinel, observerOptions, requestNextPage);

    // Nothing to observe on the last page: no element, so no spinner and no further requests.
    if (!onLoadMore || !hasNextPage) return null;
    return (
        <>
            <div ref={setSentinel} className={styles.sentinel} aria-hidden />
            {/* The "Load more" button announced itself when activated; an observer fires silently, so
                without this a screen-reader user gets no signal that more rows are on the way. Polite,
                and only while a page is actually in flight. */}
            {isLoadingMore ? (
                <span className="sr-only" role="status" aria-live="polite">
                    {t('loading')}
                </span>
            ) : null}
        </>
    );
};

export default EndOfListSentinel;
